import { Typography, styled } from '@mui/material'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfigurationCard } from 'src/generated-types/configure-positions'
import { IPositionDrawing } from 'src/generated-types/position-drawing'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import ConfigurationCardItem from './ConfigurationCardItem'
import { EmptyNote, PanelScrollArea } from './ConfigurePositionsPanel.style'

interface ConfigurationCardListProps {
  cards: ConfigurationCard[]
  drawings: IPositionDrawing[]
  highlightedKey: string | null
  onHighlight: (key: string | null) => void
  onEditCard: (card: ConfigurationCard) => void
  onRemoveCard: (card: ConfigurationCard) => void
}

const ConfigurationCardList: React.FC<ConfigurationCardListProps> = ({
  cards,
  drawings,
  highlightedKey,
  onHighlight,
  onEditCard,
  onRemoveCard,
}) => {
  const { t } = useTranslation('position')
  const cardElementsRef = useRef(new Map<string, HTMLElement>())
  const isHoveringCardRef = useRef(false)

  useEffect(() => {
    if (highlightedKey && !isHoveringCardRef.current) {
      cardElementsRef.current.get(highlightedKey)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [highlightedKey])

  const registerCardElement = (key: string) => (element: HTMLElement | null) => {
    if (element) cardElementsRef.current.set(key, element)
    else cardElementsRef.current.delete(key)
  }

  return (
    <CardListArea>
      <ListHeading>
        {t('configurations')}
        <CountPill>{cards.length}</CountPill>
      </ListHeading>
      {cards.length === 0 && <EmptyNote>{t('configure-none-yet')}</EmptyNote>}
      {cards.map(card => (
        <ConfigurationCardItem
          key={card.key}
          ref={registerCardElement(card.key)}
          card={card}
          thumbnailUrl={drawings.find(drawing => drawing.id === card.drawingId)?.fileUrl}
          highlighted={highlightedKey === card.key}
          onMouseEnter={() => {
            isHoveringCardRef.current = true
            onHighlight(card.key)
          }}
          onMouseLeave={() => {
            isHoveringCardRef.current = false
            onHighlight(null)
          }}
          onEdit={onEditCard}
          onRemove={onRemoveCard}
        />
      ))}
    </CardListArea>
  )
}

export default ConfigurationCardList

const CardListArea = styled(PanelScrollArea)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.75),
}))

const ListHeading = styled(Typography)(({ theme }) => ({
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const CountPill = styled('span')(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
  backgroundColor: getThemeColor(theme, MUIThemeColors.backgroundMain),
  borderRadius: 10,
  padding: theme.spacing(0, 1),
}))
